/**
 * API Client for typed API calls
 *
 * Provides type-safe API functions with automatic authentication.
 * Uses native fetch with environment-aware base URL.
 * For data fetching in components, use TanStack Query hooks with these functions.
 *
 * @module lib/api
 */

import type { Address } from 'viem'
import { toast } from 'react-toastify'
import bcrypt from 'bcryptjs'
import nacl from 'tweetnacl'
import { from } from '../utils/browser-buffer'

/**
 * Converts a Uint8Array to a base64 encoded string.
 * This is the browser-safe equivalent of Buffer.from(arr).toString('base64').
 */
function toBase64(uint8Array) {
  // Use btoa with String.fromCharCode to convert binary data to base64
  const binary = String.fromCharCode.apply(null, Array.from(uint8Array));
  return btoa(binary);
}

// ============================================
// Configuration
// ============================================

const getBaseUrl = () => {
  // Priority 1: Runtime environment variable (for SSR flexibility)
  // This allows changing the API URL without rebuilding the server bundle
  if (typeof window === 'undefined' || import.meta.env.SSR) {
    // In SSR, first check runtime env (process.env), then fall back to build-time VITE env
    return process.env.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:54002'
  }

  // Client-side: use build-time Vite env if available
  // In production, this will be the value baked into the client bundle
  const viteUrl = import.meta.env.VITE_API_URL
  if (viteUrl) {
    return viteUrl
  }

  // If no VITE_API_URL set, use relative URL (same origin)
  // This works if frontend and API are on the same domain (via proxy or same server)
  return ''
}

const CDN_URL = import.meta.env.VITE_CDN_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://cdn.stealthisnft.xyz')

// ============================================
// SSR API response cache
// ============================================
// During SSR, responses can be written here. On the client, authFetch
// will check this cache before hitting the network, so loader/API data
// fetched server-side is reused instead of refetched.
const SSR_API_CACHE_KEY = '__SSR_API_CACHE__'

function getSsrApiCache(): Record<string, any> | null {
  if (typeof window === 'undefined') return null
  return (window as any)[SSR_API_CACHE_KEY] || null
}

function setSsrApiCacheEntry(url: string, data: any): void {
  if (typeof window === 'undefined') return
  const cache = (window as any)[SSR_API_CACHE_KEY] || {}
  cache[url] = data
  ;(window as any)[SSR_API_CACHE_KEY] = cache
}

export function hydrateSsrApiCache(cache: Record<string, any> | null | undefined): void {
  if (typeof window === 'undefined' || !cache) return
  ;(window as any)[SSR_API_CACHE_KEY] = cache
}

// ============================================
// Helper Functions
// ============================================

/**
 * Gets authentication headers from sessionStorage
 *
 * Uses the same pattern as the old Backend() helper:
 * - authKey (account.rHash) is the actual session token
 * - Full token JSON is stored separately for UI purposes
 * - Header format: Bearer <authKey>:<userId>
 */
import { sessionStorageGet, sessionStorageSet, sessionStorageRemove } from './window-shim';

const getAuthHeaders = () => {
  const authKey = sessionStorageGet('authKey');
  const tokenStr = sessionStorageGet('token');

  if (!authKey || !tokenStr) return {};

  try {
    const parsed = JSON.parse(tokenStr);
    const userId = parsed?.account?.id;
    return {
      Authorization: `Bearer ${authKey}:${userId}`,
      'Content-Type': 'application/json',
    } as Record<string, string>;
  } catch (e) {
    console.warn('Failed to parse auth token:', e);
    return {};
  }
}

/**
 * Custom error class for API errors with status code
 */
export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

/**
 * Performs an authenticated fetch request
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<any> {
  const baseUrl = getBaseUrl()
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

  if (typeof window !== 'undefined') {
    const cached = getSsrApiCache()?.[fullUrl]
    if (cached !== undefined) {
      return cached
    }
  }

  // Set Content-Type for JSON requests if body is present and no Content-Type set
  const hasBody = options.body !== undefined && options.body !== null
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const headers: Record<string, string> = {
    ...(hasBody && !isFormData && !options.headers?.['Content-Type']
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...getAuthHeaders(),
    ...(options.headers as Record<string, string> || {}),
  }

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(`API Error ${response.status}: ${errorData.output || response.statusText}`, response.status)
  }

  // Return empty object if no content
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return {}
  }

  return response.json()
}

/**
 * Shows an error toast
 */
function showError(message: string) {
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
  })
}

// ============================================
// Types
// ============================================

export interface User {
  id: string | number
  username: string
  displayname?: string
  bio?: string
  avatar?: string
  banner?: string
  liveStream?: string
  socials?: Record<string, string>
  profile_visibility?: 'public' | 'private'
  show_socials?: boolean
  // ... add other fields from backend
}

export interface Collection {
  id: string | number
  name: string
  symbol: string
  contractAddress: string
  network: number
  description?: string
  logo?: string
  banner?: string
  // ... add other fields
}

export interface NFTMetadata {
  tokId: string | number
  /** Raw JSON string as returned by the API; call sites JSON.parse it. */
  metadata: string
  price?: string
  humanPrice?: string
  auctionID?: string | number
  for_coll_id: string | number
  // ... add other fields
}

export interface AuctionData {
  ownerAddress: string
  currentBid: string
  currentBidder?: string
  endTime: string | number
  // ... add other fields
}

// ============================================
// Typed API Functions
// ============================================

/**
 * Get user profile by ID
 */
export const getUserProfile = async (userId: string | number): Promise<User> => {
  const data = await authFetch(`/api/v1/u/${userId}`)
  const arr = data as any[]
  return arr[0] as User
}

/**
 * Get collection by database ID
 */
export const getCollectionById = async (id: string | number): Promise<Collection> => {
  const data = await authFetch('/api/v1/collbyid', {
    method: 'POST',
    body: JSON.stringify({ searchFor: id }),
  })
  const arr = data as any[]
  return arr[0] as Collection
}

/**
 * Get NFT metadata by collection ID and token ID
 */
export const getNFTMetadata = async (
  collId: string | number,
  tokenId: string | number
): Promise<NFTMetadata | null> => {
  const data = await authFetch('/api/v1/nftMetaByCidItem', {
    method: 'POST',
    body: JSON.stringify({ collid: collId, id: tokenId }),
  })
  const arr = data as any[]
  return arr[0] ?? null
}

/**
 * Get collection info (with stats)
 */
export const getCollectionInfo = async (input: {
  collId?: string | number
  canonicalName?: string
  contractAddress?: string
  chainName?: string
}): Promise<any> => {
  return authFetch('/api/v1/collInfo', {
    method: 'POST',
    body: JSON.stringify(input ?? {}),
  })
}

/**
 * Get trait rarity stats for a collection.
 * POST /api/v1/collectionRarity with { collId }
 * Returns { collectionId, totalSupply, indexedCount, indexedPct, traits: [...] }
 * where traits are pre-aggregated trait_type/trait_value rows ranked by rarity.
 * The `traits` array is empty for collections without a rarity backfill — the
 * caller decides whether to render anything.
 */
export const getCollectionRarity = async (collId: string | number): Promise<{
  collectionId?: string | number
  totalSupply?: number
  indexedCount?: number
  indexedPct?: number
  traits?: Array<{
    trait_type?: string
    trait_value?: string
    rarity_score?: number
    tier?: string
    token_count?: number
  }>
}> => {
  return authFetch('/api/v1/collectionRarity', {
    method: 'POST',
    body: JSON.stringify({ collId }),
  })
}

/**
 * Get volume info for collection or item
 * POST /api/v1/volInfo
 * Supports optional tokenId for item-specific volume
 */
export const getVolumeInfo = async (contractAddress: string, tokenId?: string | number): Promise<any> => {
  const body: any = { ctrAddr: contractAddress }
  if (tokenId !== undefined) {
    body.tid = tokenId
  }
  return authFetch('/api/v1/volInfo', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Remove a listing (delist an NFT)
 * POST /api/v1/listItem with { remove: true, ti: itemInfo }
 */
export const removeListing = async (ti: any): Promise<any> => {
  return authFetch('/api/v1/listItem', {
    method: 'POST',
    body: JSON.stringify({ remove: true, ti }),
  })
}

/**
 * Search NFTs in collection
 */
export const searchNFTs = async (
  collectionId: string | number,
  options: {
    offset?: number
    limit?: number
    search?: string
    order?: string
    chainName?: string
    canonicalName?: string
  } = {}
): Promise<{ items: NFTMetadata[]; pagination: { total: number; page: number; limit: number; totalPages: number; hasNext: boolean; hasPrev: boolean } }> => {
  const body: Record<string, unknown> = {
    page: options.offset !== undefined && options.page === undefined
      ? Math.floor(options.offset / (options.limit ?? 40)) + 1
      : (options.page ?? 1),
    search: options.search ?? '',
    limit: options.limit ?? 40,
    order: options.order ?? 'listings',
  }
  // When the collectionId is a non-numeric slug, the backend resolves the
  // numeric id from canonicalName + chainName. Pass them through so the
  // route loader can fetch NFTs in parallel with collection info.
  if (options.chainName && String(collectionId).match(/[^\d]/)) {
    body.canonicalName = options.canonicalName ?? String(collectionId)
    body.chainName = options.chainName
  }

  const data = await authFetch(`/api/v1/nftMetaById/${collectionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const items: NFTMetadata[] = Array.isArray(data) ? data : data?.items ?? data?.data ?? []
  const pagination: any = data?.pagination ?? {}
  return {
    items,
    pagination: {
      total: Number(pagination.total ?? items.length ?? 0),
      page: Number(pagination.page ?? 1),
      limit: Number(pagination.limit ?? options.limit ?? 40),
      totalPages: Number(pagination.totalPages ?? 1),
      hasNext: !!pagination.hasNext,
      hasPrev: !!pagination.hasPrev,
    },
  }
}

/**
 * Cache metadata (internal use)
 */
export const cacheMetadata = async (params: {
  hash: string
  collectionId: string | number
  clean: string
  theJson?: Record<string, any>
}): Promise<any> => {
  return authFetch('/api/v1/metaCache', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * Upload file (FormData)
 */
export const uploadFile = async (formData: FormData): Promise<any> => {
  const baseUrl = getBaseUrl()
  const authKey = sessionStorageGet('authKey')
  const tokenStr = sessionStorageGet('token')
  let userId: string | number | undefined

  if (tokenStr) {
    try {
      userId = JSON.parse(tokenStr)?.account?.id
    } catch (e) {
      console.warn('Failed to parse token for upload:', e)
    }
  }

  if (!authKey || !userId) {
    throw new Error('Authentication required for upload')
  }

  const response = await fetch(`${baseUrl}/api/v1/binupload`, {
    method: 'POST',
    body: formData,
    headers: new Headers({
      Authorization: `Bearer ${authKey}:${userId}`,
    }),
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`)
  }

  return response.json()
}

/**
 * Wallet data (for /wallet route)
 * POST /api/v1/wallet with body { address }
 */
export const getWalletData = async (address: string): Promise<any> => {
  return authFetch('/api/v1/wallet', {
    method: 'POST',
    body: JSON.stringify({ address }),
  })
}

/**
 * Get CDN URL for image
 */
export const getCdnUrl = (path: string) => `${CDN_URL}${path}`

/**
 * Modify user profile (pmod endpoint)
 * POST /api/v1/pmod
 * Fields: bio, location, socials, role (admin only), profile_visibility, show_socials, editFor (admin editing others)
 * For password changes: currentPassword, newPassword
 */
export const modifyProfile = async (profileData: {
  bio?: string
  location?: string
  socials?: Record<string, string> | string
  role?: string
  profile_visibility?: 'public' | 'private'
  show_socials?: boolean
  editFor?: string | number
  currentPassword?: string
  newPassword?: string
}): Promise<{ status: string; response?: any; error?: boolean }> => {
  return authFetch('/api/v1/pmod', {
    method: 'POST',
    body: JSON.stringify(profileData),
  })
}

/**
 * Change user password
 * POST /api/v1/pmod
 * For self-change: requires currentPassword and newPassword
 * For admin change: requires editFor (target userId) and newPassword
 *
 * IMPORTANT: The backend will bcrypt the newPassword, so we should NOT bcrypt it here.
 * We need to send:
 * - currentPassword: base64(nacl.hash(plaintext)) for verification (matches sign-in format)
 * - newPassword: base64(nacl.hash(plaintext)) only (backend will bcrypt this)
 *
 * This ensures stored password format is consistent with signup: bcrypt(base64(nacl.hash(plaintext)))
 */
export const changePassword = async (passwordData: {
  currentPassword?: string
  newPassword: string
  editFor?: string | number
}): Promise<{ status: string; response?: any; error?: boolean }> => {
  const payload: any = {
    editFor: passwordData.editFor,
  }

  // Process currentPassword if provided (self-change)
  if (passwordData.currentPassword) {
    try {
        
      const naclHash = nacl.hash(from(passwordData.currentPassword, 'utf-8'))
      payload.currentPassword = toBase64(naclHash)
    } catch (e) {
      console.error('Failed to hash current password:', e)
      throw new Error('Current password processing failed')
    }
  }

  // Send newPassword as intermediate hash (base64 of nacl.hash).
  // Backend will bcrypt.hash(this value) to store.
  try {
    const naclHash = nacl.hash(from(passwordData.newPassword, 'utf-8'))
    payload.newPassword = toBase64(naclHash)
  } catch (e) {
    console.error('Failed to process new password:', e)
    throw new Error('New password processing failed')
  }

  return authFetch('/api/v1/pmod', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ============================================
// Error Handler
// ============================================

/**
 * Returns standardized error response for HTTP status codes
 */
export const getErrorByStatusCode = (status: number) => {
  switch (status) {
    case 401:
      return { error: true, err: true, output: 'Unauthorized, please login again' }
    case 500:
      return { error: true, err: true, output: 'Internal Server Error' }
    default:
      return { error: true, err: true, output: `${status} Unknown Error` }
  }
}

/**
 * Get authors/users list with sorting and search
 * POST /api/v1/authors
 * Returns { data: [...], pagination: {...} }
 */
export const getAuthors = async (options: {
  sort?: string
  search?: string
  page?: number
  limit?: number
} = {}): Promise<{ data: any[]; pagination: any }> => {
  const response = await authFetch('/api/v1/authors', {
    method: 'POST',
    body: JSON.stringify({
      sort: options.sort ?? 'lastActive',
      search: options.search ?? '',
      page: options.page ?? 1,
      limit: options.limit ?? 20,
    }),
  })

  // Always expect { data, pagination } shape
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }

  // Fallback for legacy responses
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

/**
 * Get active listings/auctions for home page
 * POST /api/v1/listingsByMeta
 * Returns { data: [...], pagination: {...} }
 */
export const getListingsByMeta = async (options: {
  page?: number
  limit?: number
} = {}): Promise<{ data: any[]; pagination: any }> => {
  const response = await authFetch('/api/v1/listingsByMeta', {
    method: 'POST',
    body: JSON.stringify({
      page: options.page ?? 1,
      limit: options.limit ?? 20,
    }),
  })

  // Always expect { data, pagination } shape
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

// ============================================
// Authentication API Functions
// ============================================

/**
 * Get EVM nonce for login
 * POST /api/v1/evmNonce
 */
export const getEvmNonce = async (wallet: string): Promise<{ nonce: string }> => {
  return authFetch('/api/v1/evmNonce', {
    method: 'POST',
    body: JSON.stringify({ wallet }),
  })
}

/**
 * EVM login with signed message
 * POST /api/v1/evmLogin
 */
export const evmLogin = async (msg: string, sig: string): Promise<any> => {
  return authFetch('/api/v1/evmLogin', {
    method: 'POST',
    body: JSON.stringify({ msg, sig }),
  })
}

/**
 * Xumm (XRPL) login
 * POST /api/v1/xummLogin
 */
export const xummLogin = async (wallet: string, key: string): Promise<any> => {
  return authFetch('/api/v1/xummLogin', {
    method: 'POST',
    body: JSON.stringify({ wallet, key }),
  })
}

/**
 * Password sign-in
 * POST /api/v1/signin
 */
export const signIn = async (credentials: { username: string; password: string }): Promise<any> => {
  return authFetch('/api/v1/signin', {
    method: 'POST',
    body: JSON.stringify(credentials),
  })
}

/**
 * User sign-up
 * POST /api/v1/signup
 */
export const signUp = async (credentials: any): Promise<any> => {
  return authFetch('/api/v1/signup', {
    method: 'POST',
    body: JSON.stringify(credentials),
  })
}

/**
 * Account validation / password reset
 * POST /api/v1/validate with { authInfo }
 */
export const validateAccount = async (authInfo: string): Promise<any> => {
  return authFetch('/api/v1/validate', {
    method: 'POST',
    body: JSON.stringify({ authInfo }),
  })
}

/**
 * Get items for sale by a seller
 * POST /api/v1/getForSale with { sellerID, page?, limit? }
 * Returns { data: [...], pagination: {...} }
 */
export const getForSale = async (params: {
  sellerID: string | number
  page?: number
  limit?: number
}): Promise<{ data: any[]; pagination: any }> => {
  const response = await authFetch('/api/v1/getForSale', {
    method: 'POST',
    body: JSON.stringify({
      sellerID: params.sellerID,
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    }),
  })

  // Always expect { data, pagination } shape
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

/**
 * Comments API
 * POST /api/v1/comments
 * Used for both fetching comments and submitting new ones depending on payload.
 */
export const comments = async (data: any): Promise<any> => {
  return authFetch('/api/v1/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get wallet association info
 * POST /api/v1/assocWallet with { address }
 * Can also pass `set: true` to associate wallet
 */
export const getAssocWallet = async (params: {
  address: string
  set?: boolean
}): Promise<any> => {
  return authFetch('/api/v1/assocWallet', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * Get NFTs metadata by collection ID and multiple token IDs
 * POST /api/v1/nftMetaByCidItem with { collid, id: [], limit? }
 * Returns array of NFT metadata
 */
export const getNftsMetadataByCid = async (params: {
  collid: string | number
  id: (string | number)[]  // array of token IDs
  limit?: number
}): Promise<any[]> => {
  return authFetch('/api/v1/nftMetaByCidItem', {
    method: 'POST',
    body: JSON.stringify({
      collid: params.collid,
      id: params.id,
      limit: params.limit,
    }),
  }) as Promise<any[]>
}

/**
 * Get any collection metadata (fallback)
 * POST /api/v1/anyCollMeta with { collId }
 * Returns metadata for collection even if not indexed in main system
 */
export const getAnyCollMeta = async (collId: string | number): Promise<any> => {
  return authFetch('/api/v1/anyCollMeta', {
    method: 'POST',
    body: JSON.stringify({ collId }),
  })
}

/**
 * Get collection by proxy address (NFTProxyAddr)
 * POST /api/v1/collbyid with { searchFor: proxyAddress }
 * Returns array of matching collections (usually 0 or 1)
 */
export const getCollectionByProxyAddress = async (proxyAddress: string): Promise<any[]> => {
  const response = await authFetch('/api/v1/collbyid', {
    method: 'POST',
    body: JSON.stringify({ searchFor: proxyAddress }),
  })
  return Array.isArray(response) ? response : []
}

/**
 * Contact/email form submission
 * POST /api/v1/emailForm
 */
export const emailForm = async (data: {
  name: string
  email: string
  msg: string
}): Promise<{ message?: string }> => {
  return authFetch('/api/v1/emailForm', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get collection tokens owned by a wallet
 * POST /api/v1/collToksByWallet with { collid, owner }
 * Returns { nfts: [...] } or similar
 */
export const getCollToksByWallet = async (collId: string | number, owner: string): Promise<any> => {
  return authFetch('/api/v1/collToksByWallet', {
    method: 'POST',
    body: JSON.stringify({ collid: collId, owner }),
  })
}

/**
 * Create a new collection
 * POST /api/v1/newColl with collection data
 * Accepts various fields depending on context (collName, symbol, supply, uri, addr, owner, networkNumber)
 * (site_userid is derived from auth token)
 */
export const createCollection = async (collectionData: Record<string, any>): Promise<any> => {
  return authFetch('/api/v1/newColl', {
    method: 'POST',
    body: JSON.stringify(collectionData),
  })
}

/**
 * Upload to image cache (create endpoint)
 * POST /api/v1/create with { upload: { collID, edition, cachefn, clean, imageFile } }
 * This is different from file upload - it's for caching processed images
 */
export const uploadToCache = async (uploadData: {
  collID: string | number
  edition: string | number
  cachefn: string
  clean: string
  imageFile: string  // base64 data URL without the prefix
}): Promise<any> => {
  return authFetch('/api/v1/create', {
    method: 'POST',
    body: JSON.stringify({ upload: uploadData }),
  })
}

/**
 * Regular item upload (NFT creation)
 * POST /api/v1/upload with FormData (multipart/form-data)
 * This is used by the legacy upload system
 */
export const uploadItem = async (itemData: any): Promise<any> => {
  return authFetch('/api/v1/upload', {
    method: 'POST',
    body: itemData,
  });
}

/**
 * Backend fetch proxy (beFetch)
 * POST /api/v1/beFetch with { url }
 * Used to fetch external URLs through the backend (for CORS or server-side fetch)
 */
export const backendFetch = async (url: string): Promise<any> => {
  return authFetch('/api/v1/beFetch', {
    method: 'POST',
    body: JSON.stringify({ url }),
  })
}

/**
 * Get contract admin collections
 * POST /api/v1/ctrAdm with { walAddr } or { ctrAddr }
 * Returns collections where the wallet is an admin/operator
 * Returns direct array (not paginated)
 */
export const getContractAdminCollections = async (params: {
  walAddr?: string
  ctrAddr?: string
}): Promise<any[]> => {
  if (!params.walAddr && !params.ctrAddr) {
    throw new Error('Either walAddr or ctrAddr is required')
  }

  return authFetch('/api/v1/ctrAdm', {
    method: 'POST',
    body: JSON.stringify(params),
  }) as Promise<any[]>
}

/**
 * Hall of Fame statistics
 * POST /api/v1/hof with { stat, page?, limit? }
 * Returns { data: [...], pagination: {...} } for paginated leaderboard
 */
export const getHallOfFame = async (params: {
  stat: string
  page?: number
  limit?: number
}): Promise<{ data: any[]; pagination: any }> => {
  const response = await authFetch('/api/v1/hof', {
    method: 'POST',
    body: JSON.stringify({
      stat: params.stat,
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    }),
  })

  // Always expect { data, pagination } shape
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

/**
 * List item (create auction/listing)
 * POST /api/v1/listItem with listing data
 *
 * Sends the decoded `newListing` event args (auctionId, nftContract, tokenId,
 * listedBy, reserveAmount) so the backend can mirror the listing into the DB
 * immediately after the tx is mined, rather than waiting for its chainWatch
 * webhook / 30s reconciler. `contractAddr` is the AuctionManager diamond proxy
 * the backend reads `endTime` through for auction types.
 */
export const createListing = async (params: {
  event: {
    auctionId: string | number
    nftContract: string
    tokenId: string | number
    listedBy?: string
    reserveAmount?: string | number
  }
  contractAddr?: string
  saleType?: 'fixed' | 'english' | 'dutch'
  chainId?: number
  meta?: any
  [key: string]: any
}): Promise<any> => {
  return authFetch('/api/v1/listItem', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * Tell the backend a listing has been closed on-chain.
 *
 * POST /api/v1/closeItem — the counterpart to createListing. Returns 202
 * Accepted almost immediately: the backend re-derives the chain, the
 * AuctionManager, the auction id and the NFT contract/token from the receipt and
 * independently confirms the listing is really gone — but it does that in the
 * background, so this call must never be treated as "the index is updated". A
 * 202 means "received and queued", nothing more.
 *
 * `chainId` is a routing hint so the backend asks one RPC instead of sweeping
 * every configured chain (which measured 41.8s and hung the request). It is not
 * a trust boundary: all identity comes from the on-chain receipt.
 *
 * Covers every path that ends a listing: cancelSaleListing (fixed price),
 * removeListingBeforeEndTime, finalizeEarly, a Dutch settle, a buy-now, and a
 * fixed-price purchase.
 */
export const closeListing = async (params: {
  txHash: string
  chainId?: number
}): Promise<any> => {
  return authFetch('/api/v1/closeItem', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * Get collections (list)
 * POST /api/v1/collections with filters
 * Used by SecCollectionList and management pages
 * Returns { data: [...], pagination: {...} }
 * Supports either page/limit OR offset/limit for pagination
 */
export const getCollections = async (filters?: {
  byUser?: number | string
  net?: string
  fromWallet?: string
  search?: string
  page?: number
  limit?: number
  offset?: number  // legacy/alternative: provide offset directly
}): Promise<{ data: any[]; pagination: any }> => {
  const body: any = { ...filters };

  // If offset is provided but not page, convert offset to page
  if (body.offset !== undefined && body.page === undefined) {
    const limit = body.limit || 20;
    body.page = Math.floor(body.offset / limit) + 1;
    // keep offset as well? backend may not use it directly but we can drop it
    // delete body.offset;
  }

  const response = await authFetch('/api/v1/collections', {
    method: 'POST',
    body: JSON.stringify(body),
  })

  // Always expect { data, pagination } shape
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

/**
 * Get featured collections for carousel display
 * POST /api/v1/collections
 * Uses byUser: -1 to bypass isListed filter and get all collections
 * Fetches top collections ordered by popScore, limited to specified count
 * Returns array of collection objects (no pagination needed for small fixed set)
 */
export const getFeaturedCollections = async (limit: number = 12): Promise<Collection[]> => {
  const response = await authFetch('/api/v1/collections', {
    method: 'POST',
    body: JSON.stringify({
      byUser: -1,  // Bypass isListed filter to get all collections
      limit: limit,
      offset: 0,
    }),
  }) as any

  // Handle both direct array and { data: [...] } response shapes
  const collections = Array.isArray(response) ? response : (response?.data || [])
  return collections as Collection[]
}

// ============================================
// Default export (QueryClient instance)
// ============================================
/**
 * Get all available NFTs (for explore page)
 * POST /api/v1/all
 * Supports pagination with `page` and `limit` (or legacy `from` offset)
 * Returns { data: [...], pagination: {...} }
 */
export const getAllItems = async (options: {
  page?: number
  limit?: number
  from?: number  // legacy offset parameter
} = {}): Promise<{ data: any[]; pagination: any }> => {
  // Support both new pagination (page/limit) and legacy offset (from)
  const body: any = {}
  if (options.from !== undefined) {
    body.from = options.from
  } else {
    body.page = options.page ?? 1
    body.limit = options.limit ?? 20
  }

  const response = await authFetch('/api/v1/all', {
    method: 'POST',
    body: JSON.stringify(body),
  })

  // Handle both direct array and { data, pagination } responses
  if (response && typeof response === 'object' && 'data' in response) {
    return response as { data: any[]; pagination: any }
  }
  return { data: Array.isArray(response) ? response : [], pagination: null }
}

// Note: The QueryClient should be created in the provider, not here.
// Use the API functions above for data fetching.